import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, Observer } from 'rxjs';
import { AuthService } from './auth.service';
import { EnvironmentService } from './environment.service';
import { PGUtilities } from '../utilities/pg-utilities';

class ApiInterceptorQueueItem { 
    req: HttpRequest<any>;
    next: HttpHandler;
    observer: Observer<HttpEvent<any>>;
}

@Injectable({
    providedIn: 'root'
})
export class ApiInterceptor implements HttpInterceptor {

    constructor(private environmentService:EnvironmentService, private authService:AuthService) { }

    private _isReleaseRequest(req: HttpRequest<any>) {
        return /^assets\/release\.json$/.test(req.url)
    }

    private _isAssetsRequest(req: HttpRequest<any>) {
        return /^assets\/[^\?]*$/.test(req.url)
    }

    private _isAPIRequest(req: HttpRequest<any>) {
        return req.url.startsWith(this.environmentService.environment.APIUrl)
    }

    private _isImporterRequest(req: HttpRequest<any>) {
        let _importerURL = this.environmentService.environment.ImporterURL;
        return _importerURL != null && _importerURL != '' && req.url.startsWith(_importerURL)
    }

    private _isQueuedAuthRequest(req: HttpRequest<any>) {
        return this._isAPIRequest(req) || this._isImporterRequest(req)
    }

    private _isAPIPublicRequest(req: HttpRequest<any>) {
        if(this._isAPIRequest(req)) {
            let _clearURL = req.url.replace(this.environmentService.environment.APIUrl, '')
            
            return _clearURL.startsWith('/public') || _clearURL.startsWith('/environment')
        }
        
        return false;
    }

    private _isRefreshRequest(req: HttpRequest<any>) {
        return this._isAPIRequest(req) && /\/refresh\?refresh_token=/.test(req.urlWithParams);
    }

    private async _checkAPIRequestAuthTokenAndRealm(req: HttpRequest<any>) {
        if(this._isAPIPublicRequest(req)) {
            return req;
        }
        else {
            let _cToken = await this.authService.getAuthToken(this._isRefreshRequest(req));
        
            if(_cToken != null) {
                let _reqUrl = req.urlWithParams
    
                return req.clone({
                    url: _reqUrl,
                    setHeaders: {
                        'Authorization': 'Bearer ' + _cToken
                    }
                });
            }
            else {
                return req;
            }
        }
    }

    private _calculateAPIRequestNextDelay(req:HttpRequest<any>, resp:HttpResponse<any>, time:number) {
        let _rateLimit = parseInt(resp.headers.get('x-ratelimit-limit'));
        let _rateRemaining = parseInt(resp.headers.get('x-ratelimit-remaining'));

        if(!isNaN(_rateLimit) && !isNaN(_rateRemaining)) {
            let _rateAverage = 60 / _rateLimit * 1000;
            let _ratePoint = 1 - (_rateRemaining / _rateLimit);

            let _nextDelay = Math.floor(Math.pow(_ratePoint, 2) * 4 * _rateAverage);
            let _cleanDelay = Math.max(1, _nextDelay - time);

            //console.log('API Rate limit ' + Math.floor(_ratePoint * 100) + '% Delay (' + _nextDelay + ' - ' + time + ') = ' + _cleanDelay);

            return _cleanDelay;
        }
        else {
            let _nextDelay = 250;
            let _cleanDelay = Math.max(1, _nextDelay - time);
            //console.log('API Fixed Delay (' + _nextDelay + ' - ' + time + ') = ' + _cleanDelay);

            return _cleanDelay;
        }
    }

    private _APIRequestQueue:Array<ApiInterceptorQueueItem> = []

    private _APIRetryRequestCount:number = null;
    private _APIRequestQueueLock = false;
    private _checkAPIRequestQueue() {
        if(!this._APIRequestQueueLock) {
            let _ctx = this._APIRequestQueue[0];

            if(_ctx != null) {
                this._APIRequestQueueLock = true;

                let _timeStart = Date.now();

                this._checkAPIRequestAuthTokenAndRealm(_ctx.req).then((req) => {
                    _ctx.req = req;

                    _ctx.next.handle(_ctx.req).subscribe((resp) => {
                        if(resp.type == HttpEventType.Response) {
                            let _delay = this._calculateAPIRequestNextDelay(_ctx.req, resp, Date.now() - _timeStart);
                            this._onAPIRequestComplete(this._onRequestSuccess(_ctx, resp), _delay);
                        }
                        else {
                            _ctx.observer.next(resp);
                        }
                    }, (error) => {
                        if(error.status == 401 && this.authService.hasAuthToken()) {
                            setTimeout(() => {
                                this.authService.tryRefreshToken().then(() => {
                                    this._APIRequestQueueLock = false;
                                    this._checkAPIRequestQueue();
                                })
                            }, 250)
                        }
                        else {
                            let _mustRetry = error.status == 429;
                            
                            if(_mustRetry) {
                                this._APIRetryRequestCount++;
                                if(this._APIRetryRequestCount > 15) {
                                    _mustRetry = false;
                                }
                            }
        
                            if(_mustRetry) {
                                setTimeout(() => {
                                    this._APIRequestQueueLock = false;
                                    this._checkAPIRequestQueue();
                                }, 5000)
                            }
                            else {
                                this._APIRetryRequestCount = 0;
        
                                let _delay = this._calculateAPIRequestNextDelay(_ctx.req, error, Date.now() - _timeStart);
                                this._onAPIRequestComplete(this._onAPIRequestError(_ctx, error), _delay);
                            }
                        }
                    })
                })
            }   
        }
    }

    private _isAPIRequestSameContext(ctxa:ApiInterceptorQueueItem, ctxb:ApiInterceptorQueueItem) {
        if(ctxa == ctxb) return true;
        else { // NB: lo applico solo alle GET
            if(ctxa.req.method.toUpperCase() == 'GET' && ctxb.req.method.toUpperCase() == 'GET' && ctxa.req.urlWithParams == ctxb.req.urlWithParams) {
                /*if((ctxa.req.body == null && ctxb.req.body == null) || (JSON.stringify(ctxa.req.body) == JSON.stringify(ctxb.req.body))) return true
                else return false;*/
                return true
            }
        }
    }

    private _onRequestSuccess(ctx:ApiInterceptorQueueItem, event:HttpEvent<any>) {
        let _retVal:Array<ApiInterceptorQueueItem> = [];

        for(let i = this._APIRequestQueue.length - 1; i >= 0; i--) {
            if(this._isAPIRequestSameContext(ctx, this._APIRequestQueue[i])) {
                _retVal.push(this._APIRequestQueue[i]);
                this._APIRequestQueue[i].observer.next(event);
            }
        }

        return _retVal;
    }

    private _onAPIRequestError(ctx:ApiInterceptorQueueItem, error:any) {
        let _retVal:Array<ApiInterceptorQueueItem> = [];

        for(let i = this._APIRequestQueue.length - 1; i >= 0; i--) {
            if(this._isAPIRequestSameContext(ctx, this._APIRequestQueue[i])) {
                _retVal.push(this._APIRequestQueue[i]);
                this._APIRequestQueue[i].observer.error(error);
            }
        }

        return _retVal;
    }

    private _onAPIRequestComplete(ctxList:Array<ApiInterceptorQueueItem>, delay:number) {
        this._APIRetryRequestCount = 0;

        setTimeout(() => {
            for(let _ctx of ctxList) {
                _ctx.observer.complete()

                let _index = this._APIRequestQueue.indexOf(_ctx);
                if(_index != -1) {
                    this._APIRequestQueue.splice(_index, 1);
                }
            }
            this._APIRequestQueueLock = false;
            this._checkAPIRequestQueue();
        }, delay)
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if(this._isReleaseRequest(req)) {
            req = req.clone({
                setParams: { 't': Date.now().toString() }
            });
        }
        else if(this._isAssetsRequest(req)) {
            req = req.clone({
                setParams: { 'v': this.environmentService.getBuildTime() }
            });
        }
        else if(this._isAPIRequest(req)) {
            req = req.clone({
                setHeaders: { 'PG-TransactionId': PGUtilities.getRandomId() }
            });
        }

        if(this._isQueuedAuthRequest(req)) {
            if(this._isRefreshRequest(req)) {
                return new Observable<HttpEvent<any>>((observer) => {
                    this._checkAPIRequestAuthTokenAndRealm(req).then((req) => {
                        next.handle(req).subscribe(observer);
                    });
                });
            }
            else {
                return new Observable<HttpEvent<any>> ((observer) => {
                    this._APIRequestQueue.push({ 
                        req: req, 
                        next: next, 
                        observer: observer 
                    });
                    this._checkAPIRequestQueue();
                })
            }
        }
        else {
            return next.handle(req);
        }
    }
}
